import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpService } from 'src/app/http.service';
import * as QRCode from 'qrcode';

/**
 * Web Chat Settings — a dedicated, simplified manager for the two browser/account-based
 * channels (Web WhatsApp via QR, Web Telegram via user account). Unlike the full
 * Chat Channels manager this screen needs NO MultiChat Server / VPS: every provider
 * runs in-process. Bot-token Telegram and other channels stay in /chat-channel.
 */
@Component({
  selector: 'app-web-chat-settings',
  templateUrl: './web-chat-settings.component.html',
  styleUrls: ['./web-chat-settings.component.scss']
})
export class WebChatSettingsComponent implements OnInit, OnDestroy {

  public company_uuid: string = '';
  public channels: any[] = [];
  public loading: boolean = false;
  private pollInterval: any = null;

  // ── Web WhatsApp add/QR ──
  public showWaForm: boolean = false;
  public waName: string = 'WhatsApp';
  public waConnecting: boolean = false;
  public waChannelId: number | null = null;     // channel currently showing a QR
  public waQrDataUrl: string = '';
  public waStatus: string = '';

  // ── Web Telegram (user account) multi-step add ──
  public showTgForm: boolean = false;
  public tgApiId: string = '';
  public tgApiHash: string = '';
  public tgPhone: string = '';
  public tgName: string = 'Telegram Web';
  public tgCode: string = '';
  public tgPassword: string = '';
  public tgChannelId: number | null = null;
  public tgAuthState: string = '';   // '', 'awaiting_phone', 'awaiting_code', 'awaiting_password', 'connected'
  public tgConnecting: boolean = false;
  public tgUser: any = null;

  // ── Inline name edit ──
  public editingId: number | null = null;
  public editingName: string = '';

  constructor(private http: HttpService) {
    this.company_uuid = this.http.getStaticSelectedCompany();
  }

  ngOnInit(): void {
    this.loadChannels();
  }

  ngOnDestroy(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
  }

  async loadChannels() {
    this.loading = true;
    const rtn: any = await this.http.post('/admin/chat/channel/list');
    // This screen only manages the two web/account channels.
    this.channels = (rtn?.channels || []).filter(
      (c: any) => c.channel === 'whatsapp' || c.channel === 'telegram_web'
    );
    this.loading = false;
  }

  // ────────────────────────── Web WhatsApp ──────────────────────────

  openWaForm() {
    this.showWaForm = true;
    this.waName = 'WhatsApp';
  }

  cancelWaForm() {
    this.showWaForm = false;
    this.waName = 'WhatsApp';
  }

  /** Create a new in-process WhatsApp channel (no multichat_server_id) and start the QR flow. */
  async createWhatsApp() {
    this.waConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/whatsapp/init', {
      name: (this.waName || 'WhatsApp').trim(),
      config: {}
    });
    this.waConnecting = false;
    if (rtn?.status && rtn.channel_id) {
      this.showWaForm = false;
      await this.loadChannels();
      this.openQr(rtn.channel_id);
    } else {
      this.http.notify(rtn?.message || 'Failed to initialize WhatsApp');
    }
  }

  /** Re-initialize an existing channel and (re)open its QR. */
  async reconnectWhatsApp(ch: any) {
    const rtn: any = await this.http.post('/admin/chat/whatsapp/init', { channel_id: ch.id });
    this.http.notify(rtn?.message || 'Reconnecting...');
    await this.loadChannels();
    this.openQr(ch.id);
  }

  /** Open the QR panel for a channel and begin polling status + QR. */
  openQr(channel_id: number) {
    this.waChannelId = channel_id;
    this.waQrDataUrl = '';
    this.waStatus = 'loading';
    this.pollWhatsApp();
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.pollInterval = setInterval(() => this.pollWhatsApp(), 5000);
  }

  closeQr() {
    this.waChannelId = null;
    this.waQrDataUrl = '';
    this.waStatus = '';
    if (this.pollInterval) { clearInterval(this.pollInterval); this.pollInterval = null; }
  }

  private async pollWhatsApp() {
    if (!this.waChannelId) return;
    const res: any = await this.http.post('/admin/chat/whatsapp/status', { channel_id: this.waChannelId });
    this.waStatus = res?.connection_status || 'unknown';

    if (this.waStatus === 'connected') {
      this.waQrDataUrl = '';
      this.http.notify('WhatsApp connected');
      if (this.pollInterval) { clearInterval(this.pollInterval); this.pollInterval = null; }
      await this.loadChannels();
      return;
    }

    if (this.waStatus === 'qr_needed') {
      const qrRes: any = await this.http.post('/admin/chat/whatsapp/qr', { channel_id: this.waChannelId });
      if (qrRes?.status && qrRes.qr) {
        this.waQrDataUrl = await QRCode.toDataURL(qrRes.qr, { width: 280, margin: 2 });
      }
    }
  }

  // ────────────────────────── Web Telegram ──────────────────────────

  openTgForm() {
    this.showTgForm = true;
    this.tgApiId = '';
    this.tgApiHash = '';
    this.tgPhone = '';
    this.tgName = 'Telegram Web';
    this.tgCode = '';
    this.tgPassword = '';
    this.tgChannelId = null;
    this.tgAuthState = '';
    this.tgUser = null;
  }

  cancelTgForm() {
    this.showTgForm = false;
    this.tgApiId = '';
    this.tgApiHash = '';
    this.tgPhone = '';
    this.tgCode = '';
    this.tgPassword = '';
    this.tgChannelId = null;
    this.tgAuthState = '';
    this.tgUser = null;
  }

  async submitTgInit() {
    if (!this.tgApiId.trim() || !this.tgApiHash.trim()) return;
    this.tgConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/init', {
      api_id: this.tgApiId.trim(),
      api_hash: this.tgApiHash.trim(),
      name: (this.tgName || 'Telegram Web').trim()
    });
    this.tgConnecting = false;

    if (rtn?.status) {
      this.tgChannelId = rtn.channel_id;
      this.tgAuthState = rtn.auth_state || 'awaiting_phone';
      if (rtn.auth_state === 'connected') {
        this.tgUser = rtn.user;
        this.http.notify('Connected as ' + (rtn.user?.firstName || 'user'));
        this.cancelTgForm();
        await this.loadChannels();
      }
    } else {
      this.http.notify(rtn?.message || 'Failed');
    }
  }

  async reconnectTg(ch: any) {
    this.tgConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/init', { channel_id: ch.id });
    this.tgConnecting = false;
    if (rtn?.auth_state === 'connected') {
      this.http.notify('Telegram Web reconnected');
    } else if (rtn?.status) {
      this.showTgForm = true;
      this.tgChannelId = ch.id;
      this.tgAuthState = rtn.auth_state || 'awaiting_phone';
    } else {
      this.http.notify(rtn?.message || 'Failed');
    }
    await this.loadChannels();
  }

  async submitTgPhone() {
    if (!this.tgPhone.trim() || !this.tgChannelId) return;
    this.tgConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/send-code', {
      channel_id: this.tgChannelId,
      phone: this.tgPhone.trim()
    });
    this.tgConnecting = false;
    if (rtn?.status) {
      this.tgAuthState = 'awaiting_code';
      this.http.notify(rtn.message || 'Code sent');
    } else {
      this.http.notify(rtn?.message || 'Failed to send code');
    }
  }

  async submitTgCode() {
    if (!this.tgCode.trim() || !this.tgChannelId) return;
    this.tgConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/verify-code', {
      channel_id: this.tgChannelId,
      code: this.tgCode.trim()
    });
    this.tgConnecting = false;
    if (rtn?.status) {
      if (rtn.auth_state === 'awaiting_password') {
        this.tgAuthState = 'awaiting_password';
        this.http.notify('2FA password required');
      } else {
        this.tgUser = rtn.user;
        this.http.notify('Authenticated as ' + (rtn.user?.firstName || 'user'));
        this.cancelTgForm();
        await this.loadChannels();
      }
    } else {
      this.http.notify(rtn?.message || 'Invalid code');
    }
  }

  async submitTgPassword() {
    if (!this.tgPassword.trim() || !this.tgChannelId) return;
    this.tgConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/verify-password', {
      channel_id: this.tgChannelId,
      password: this.tgPassword.trim()
    });
    this.tgConnecting = false;
    if (rtn?.status) {
      this.tgUser = rtn.user;
      this.http.notify('Authenticated as ' + (rtn.user?.firstName || 'user'));
      this.cancelTgForm();
      await this.loadChannels();
    } else {
      this.http.notify(rtn?.message || 'Wrong password');
    }
  }

  // ────────────────────────── Shared row actions ──────────────────────────

  async disconnectChannel(ch: any) {
    if (!confirm('Disconnect this channel?')) return;
    const rtn: any = await this.http.post('/admin/chat/' + ch.channel + '/disconnect', { channel_id: ch.id });
    this.http.notify(rtn?.message || 'Disconnected');
    if (this.waChannelId === ch.id) this.closeQr();
    await this.loadChannels();
  }

  async deleteChannel(ch: any) {
    if (!confirm(`Delete channel "${ch.name}"? This will disconnect and remove it permanently.`)) return;
    const rtn: any = await this.http.post('/admin/chat/channel/delete', { id: ch.id });
    if (rtn?.status) {
      if (this.waChannelId === ch.id) this.closeQr();
      this.channels = this.channels.filter((c: any) => c.id !== ch.id);
      this.http.notify('Channel deleted');
    } else {
      this.http.notify(rtn?.message || 'Failed to delete');
    }
  }

  startEdit(ch: any) {
    this.editingId = ch.id;
    this.editingName = ch.name;
  }

  cancelEdit() {
    this.editingId = null;
    this.editingName = '';
  }

  async saveEdit(ch: any) {
    if (!this.editingName.trim()) return;
    const rtn: any = await this.http.post('/admin/chat/channel/update', { id: ch.id, name: this.editingName.trim() });
    if (rtn?.status) {
      ch.name = this.editingName.trim();
      this.http.notify('Channel name updated');
    } else {
      this.http.notify(rtn?.message || 'Failed to update');
    }
    this.editingId = null;
    this.editingName = '';
  }

  // ────────────────────────── Display helpers ──────────────────────────

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'connected': return 'bg-success';
      case 'qr_needed': return 'bg-warning text-dark';
      case 'awaiting_phone':
      case 'awaiting_code':
      case 'awaiting_password': return 'bg-info text-dark';
      case 'disconnected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  channelLabel(channel: string): string {
    return channel === 'whatsapp' ? 'Web WhatsApp' : 'Web Telegram';
  }

  channelIcon(channel: string): string {
    return channel === 'whatsapp' ? '📱' : '✈️';
  }
}
